import { t } from '@lingui/macro';
import React, { useState, useCallback } from 'react';

import { type IdentityResponse } from '@api/v4/private/people_tagging/faces';
import { SuggestedNamesList } from '@components/people_tags/common/SuggestedNamesList';

import { Person } from '@components/people_tags/customPeopleTagTypes';

import { PeopleTagScopeConfirmModal } from './PeopleTagScopeConfirmModal';
import {
  PersonTag,
  PersonImage,
  PersonName,
  PersonNameText,
  RemovePersonButton,
  CustomDropdown,
  DropdownHeader,
  DropdownIcon,
  InputStyled,
  FaceThumbnailImage
} from './styles/PeopleInAssetsComponents';

// Face data structure used internally
export interface FaceData {
  identityId?: string;
  faceId?: string;
  name: string;
  assetUrl: string;
  faceThumbnailUrl: string;
  isHidden: boolean;
}

interface PeopleTagEditFieldProps {
  face: FaceData;
  identities: IdentityResponse[];
  onSplitIdentity: (identityId: string, faceId: string, name?: string, targetIdentityId?: string) => void;
  onMergeIdentities: (sourceIdentityId: string, destinationIdentity: Person, faceId: string) => void;
  onUpdateIdentityName: (identityId: string, name: string, faceId: string) => void;
}

export const PeopleTagEditField: React.FC<PeopleTagEditFieldProps> = ({
  face,
  identities,
  onSplitIdentity,
  onMergeIdentities,
  onUpdateIdentityName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [showScopeConfirmModal, setShowScopeConfirmModal] = useState(false);
  const [allAssetsScopeAction, setAllAssetsScopeAction] = useState<() => void>(() => () => {});
  const [thisAssetScopeAction, setThisAssetScopeAction] = useState<() => void>(() => () => {});
  const hasName = face.name && face.name.trim() !== '';

  // Map identities to suggestions format
  const getFilteredSuggestions = useCallback((name: string) => {
    const filteredIdentities = name?.trim() ? identities.filter(
      (identity) => identity.name && identity.name?.toLowerCase().includes(name.toLowerCase())
    ) : identities.filter((identity) => identity.name?.trim().length > 0);

    return filteredIdentities.map((identity) => ({
      face_uri: identity.identity_thumbnail_url || '',
      person_id: identity.id,
      tag_name: identity.name,
      face_count: identity.face_count
    }));
  }, [identities]);

  const handleSearchInputChange = useCallback((value: string): void => {
    setSearchInput(value);
    setIsOpen(true);
  }, []);

  const handleKeyUp = useCallback((e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') {
      setIsOpen(false);
      setAllAssetsScopeAction(() => () => onUpdateIdentityName(face.identityId, searchInput, face.faceId));
      setThisAssetScopeAction(() => () => onSplitIdentity(face.identityId, face.faceId, searchInput, null));
      setShowScopeConfirmModal(true);
    }

    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchInput('');
      (e.target as HTMLInputElement).blur();
    }
  }, [onUpdateIdentityName, onSplitIdentity, face.identityId, face.faceId, searchInput]);

  const onIdentitySelected = useCallback((selectedIdentity: Person): void => {
    setIsOpen(false);
    setAllAssetsScopeAction(() => () => onMergeIdentities(face.identityId, selectedIdentity, face.faceId));
    setThisAssetScopeAction(
      () => () => onSplitIdentity(face.identityId, face.faceId, selectedIdentity.tag_name, selectedIdentity.person_id)
    );
    setShowScopeConfirmModal(true);
  }, [face.faceId, face.identityId, onMergeIdentities, onSplitIdentity]);

  const onRemoveFaceName = (): void => {
    setIsOpen(false);
    setSearchInput('');
    setAllAssetsScopeAction(() => () => onUpdateIdentityName(face.identityId, '', face.faceId));
    setThisAssetScopeAction(() => () => onSplitIdentity(face.identityId, face.faceId, '', null));
    setShowScopeConfirmModal(true);
  };

  const toggleDropdown = useCallback((): void => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleModalClose = (): void => {
    setShowScopeConfirmModal(false);
  };

  const handlePrimaryAction = (): void => {
    allAssetsScopeAction();
    setShowScopeConfirmModal(false);
    setSearchInput('');
  };

  const handleSecondaryAction = (): void => {
    thisAssetScopeAction();
    setShowScopeConfirmModal(false);
    setSearchInput('');
  };

  return (
    <>
      <PersonTag>
        <PersonImage>
          <FaceThumbnailImage
            alt={hasName ? face.name : t`Unidentified person`}
            src={face.faceThumbnailUrl}
          />
        </PersonImage>
        <PersonName>
          {hasName ? (
            <>
              <PersonNameText>{face.name}</PersonNameText>
              <RemovePersonButton
                aria-label={t`Remove ${face.name}`}
                onClick={(): void => onRemoveFaceName()}
              >
                <span className="icon bff-close" />
              </RemovePersonButton>
            </>
          ) : (
            <CustomDropdown>
              <DropdownHeader>
                <InputStyled
                  onChange={(e): void => handleSearchInputChange(e.target.value)}
                  onKeyUp={(e): void => handleKeyUp(e)}
                  placeholder={t`Name person`}
                  type="text"
                  value={searchInput}
                />
                <DropdownIcon onClick={toggleDropdown}>
                  <span className={`icon ${isOpen ? 'bff-caret-up' : 'bff-caret-down'}`} />
                </DropdownIcon>
              </DropdownHeader>

              {isOpen && (
                <SuggestedNamesList
                  handleOnSelectTargetPerson={onIdentitySelected}
                  listClasses="suggested-identity-names-list"
                  showCreateOption={false}
                  suggestions={getFilteredSuggestions(searchInput)}
                  typedName={searchInput}
                />
              )}
            </CustomDropdown>
          )}
        </PersonName>
      </PersonTag>
      <PeopleTagScopeConfirmModal
        isOpen={showScopeConfirmModal}
        onClose={handleModalClose}
        onPrimaryAction={handlePrimaryAction}
        onSecondaryAction={handleSecondaryAction}
      />
    </>
  );
};
